import { combineReducers, configureStore } from '@reduxjs/toolkit'
import { createMigrate, persistReducer } from 'redux-persist'
import storage from 'redux-persist/lib/storage'

import { AssistantReducer } from './reducers/assistant'
import { NotificationsReducer } from './reducers/notifications'
import { SettingsReducer, initialState as settingsInitialState } from './reducers/settings'
import { WorkOrderSearchReducer } from './reducers/workOrderSearch'

const reducer = combineReducers({
  assistant: AssistantReducer,
  notifications: NotificationsReducer,
  settings: SettingsReducer,
  workOrderSearch: WorkOrderSearchReducer,
})

const migrations = {
  1: (state: any) => ({ ...state, settings: settingsInitialState }),
  2: (state: any) => ({
    ...state,
    settings: {
      ...settingsInitialState,
      ...state?.settings,
      isAppSidebarOpen: state?.settings?.isAppSidebarOpen ?? settingsInitialState.isAppSidebarOpen,
    },
  }),
  3: (state: any) => ({
    ...state,
    settings: {
      ...settingsInitialState,
      ...state?.settings,
      kanbanFilters: {
        ...settingsInitialState.kanbanFilters,
        ...state?.settings?.kanbanFilters,
      },
    },
  }),
  // The `isOnlyShowingMyAssignedJobs` boolean became the `assignedJobsFilter`
  // tri-state. Map the old `true` (reportable-now behaviour) onto its equivalent.
  4: (state: any) => {
    const pages = ['planning', 'workshop', 'handoff'] as const
    const oldFilters = state?.settings?.kanbanFilters ?? {}
    const kanbanFilters = Object.fromEntries(
      pages.map(page => {
        const old = oldFilters[page] ?? {}
        return [
          page,
          {
            whitelistedWorkflowStates: old.whitelistedWorkflowStates ?? [],
            assignedJobsFilter: old.isOnlyShowingMyAssignedJobs ? 'reportableNow' : 'all',
          },
        ]
      }),
    )
    return {
      ...state,
      settings: { ...settingsInitialState, ...state?.settings, kanbanFilters },
    }
  },
  5: (state: any) => {
    const pages = ['planning', 'workshop', 'handoff'] as const
    const oldFilters = state?.settings?.kanbanFilters ?? {}
    const kanbanFilters = Object.fromEntries(
      pages.map(page => [
        page,
        {
          ...settingsInitialState.kanbanFilters[page],
          ...oldFilters[page],
          sortOrder: 'lastMoved',
        },
      ]),
    )
    return {
      ...state,
      settings: { ...settingsInitialState, ...state?.settings, kanbanFilters },
    }
  },
}

const persistedReducer = persistReducer(
  {
    key: 'root',
    storage,
    version: 5,
    migrate: createMigrate(migrations, { debug: false }),
    blacklist: ['workOrderSearch'],
  },
  reducer,
)

const _makeStore = () =>
  configureStore({
    middleware: getDefaultMiddleware => getDefaultMiddleware({ serializableCheck: false }),
    reducer: persistedReducer,
  })

let store: ReturnType<typeof _makeStore>

export const makeStore = () => {
  if (store) return store

  const newStore = _makeStore()
  store = newStore

  return newStore
}

export type RootState = ReturnType<ReturnType<typeof makeStore>['getState']>

export type AppDispatch = ReturnType<typeof makeStore>['dispatch']
